import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { AgGridAngular } from 'ag-grid-angular';
import { Column } from 'ag-grid-community';

import { NSCustomizedReportingGridConstants } from '@ruby/configs/customized-reporting-grid.constants';
import { ICRFavoriteReport } from '@ruby/modules/customized-reporting/models/report.interface';
import { IUTDetention, IUTDetentionDispute } from '@ruby/modules/unit-train/models/unit-train-metrics.interface';
import { IUnitTrainFilters, IUTFiltersConfigurations } from '@ruby/modules/unit-train/models/unit-train.interface';
import { EGridType } from '@ruby/shared/enums/grid.enums';
import { EUTMetricType } from '@ruby/shared/enums/unit-train.enums';
import { IGridConfigurations } from '@ruby/shared/models/commons/grid.interface';
import { GridCustomizedReportingService } from '@ruby/shared/services/grid-customized-reporting.service';

@Component({
  selector: 'ng-ut-results-detentions',
  templateUrl: './ut-results-detentions.component.html',
  styleUrls: ['./ut-results-detentions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UTResultsDetentionsComponent implements OnChanges {
  @Input() gridConfigs?: IGridConfigurations;
  @Input() filtersConfigs?: Partial<IUTFiltersConfigurations>;
  @Input() loading!: boolean;
  @Input() data: Array<IUTDetentionDispute | IUTDetention> = [];
  @Input() type!: EUTMetricType;
  @Output() makeAdvanceSearch = new EventEmitter<IUnitTrainFilters>();
  @ViewChild('expansionPanelFilters') filtersPanel!: MatExpansionPanel;
  filters?: IUnitTrainFilters;
  grid!: AgGridAngular;
  gridInitialized = false;
  metricType = EUTMetricType;

  constructor(private customizedReportingService: GridCustomizedReportingService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filtersConfigs?.currentValue && this.filtersPanel) {
      this.filtersPanel.expanded = true;
    }
    if (changes.gridConfigs?.currentValue) {
      this.gridInitialized = false;
      setTimeout(() => { this.gridInitialized = true; }, 100);
    }
  }

  /**
   * Save Filters
   *
   * @summary set filters and makes a call to search with filters
   * @param $filters: IUnitTrainFilters
   * @return void
   */
  saveFilters($filters: IUnitTrainFilters): void {
    this.filtersPanel.expanded = false;
    this.filters = $filters;
    this.emitMakeAdvanceSearch();

    if (this.type === EUTMetricType.REVIEW_DETENTION) {
      const actionColumn: Column | undefined = this.grid.columnApi.getAllGridColumns().find((c: Column) => c.getColDef().headerName === 'Action');
      if (actionColumn) {
        this.grid.columnApi.setColumnVisible(actionColumn.getColId(), $filters.billStatus === 'N');
      }
    }

    if (this.gridConfigs) {
      this.grid.gridOptions.context.timeFormat = $filters.timeFormat;
      this.grid.gridOptions.context.mainComponent = this;
      this.grid.api.refreshHeader();
    }
  }

  /**
   * Emit Make Advance Search
   *
   * @summary emit an event to make a search
   * @return void
   */
  emitMakeAdvanceSearch(): void {
    if (this.filters) {
      this.makeAdvanceSearch.emit(this.filters);
    }
  }

  /**
   * Set Grid
   *
   * @summary Sets the grid instance
   * @param $grid: AgGridAngular
   * @returns void
   */
  setGrid($grid: AgGridAngular): void {
    this.grid = $grid;
  }

  /**
   * Create Customized Report
   *
   * @summary call create customized report
   * @param $filters: { filters: Partial<IUnitTrainFilters>; formValue: string }
   * @returns void
   */
  createReport($filters: { filters: Partial<IUnitTrainFilters>; formValue: string; selectedReport?: ICRFavoriteReport }): void {
    this.customizedReportingService.createCustomizedReport(
      this.gridConfigs!.gridTitle,
      this.gridConfigs!.gridName,
      NSCustomizedReportingGridConstants.getGridData(this.gridConfigs!.gridName, this.grid),
      [],
      NSCustomizedReportingGridConstants.getGridOptions(this.gridConfigs!.gridName as EGridType, $filters.formValue, JSON.stringify($filters.filters)),
      $filters.selectedReport
    );
  }
}
